"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cn } from "@repo/ui/utils.ts";
import { PortableText, type PortableTextReactComponents, type PortableTextBlock } from "next-sanity";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@repo/ui/accordion";
import { asPortableText, hardBreakHandler, linkMarkHandler, onMissingPortableTextComponent, proseBaseClasses } from "./shared";
import { DefaultTableBlock } from "./rich-text-table.component";
import { parseChildrenToSlug, getI18nString } from "@/lib/utils";
import type { RichTextTableBlock } from "@/lib/sanity/sanity.types";

/**
 * Dedicated component for the `richTextAccordion` schema.
 *
 * Schema permits everything `richTextRight` allows (`normal`, `h3`,
 * `blockquote`, lists, `strong`, `em`, `link`) plus an `accordion` block that
 * embeds a referenced FAQ collection.
 */

interface DereferencedFaq {
  /** The array member's Sanity `_key` — unique even when the same FAQ is referenced twice. */
  key?: string;
  _id?: string;
  headline?: Array<{
    _key: string;
    value?: string;
  }>;
  bodyCopy?: unknown;
}
interface AccordionBlockValue {
  _type: "accordion";
  _key: string;
  faqCollection?: {
    name?: string;
    faqs?: DereferencedFaq[];
  };
}
function isAccordionBlock(block: PortableTextBlock): block is PortableTextBlock & AccordionBlockValue {
  return block._type === "accordion";
}

// Render consecutive accordion blocks as one <Accordion> so dividers don't double up between them.
function mergeAdjacentAccordions(blocks: PortableTextBlock[]): PortableTextBlock[] {
  const merged: PortableTextBlock[] = [];
  for (const block of blocks) {
    const prev = merged[merged.length - 1];
    if (isAccordionBlock(block) && prev && isAccordionBlock(prev)) {
      prev.faqCollection = {
        ...prev.faqCollection,
        faqs: [...(prev.faqCollection?.faqs ?? []), ...(block.faqCollection?.faqs ?? [])]
      };
    } else if (isAccordionBlock(block)) {
      const clone: PortableTextBlock & AccordionBlockValue = {
        ...block,
        faqCollection: {
          ...block.faqCollection,
          faqs: [...(block.faqCollection?.faqs ?? [])]
        }
      };
      merged.push(clone);
    } else {
      merged.push(block);
    }
  }
  return merged;
}
const faqBodyComponents: Partial<PortableTextReactComponents> = {
  block: {
    h3: ({
      children,
      value
    }) => {
      const slug = parseChildrenToSlug(value.children);
      return <h3 id={slug} className="typography-component-sm-medium desktop:typography-component-md-medium uppercase text-text-primary">
          {children}
        </h3>;
    },
    blockquote: ({
      children
    }) => <blockquote data-blockquote className="border-l border-border-default pl-3 typography-body-xs-regular desktop:typography-body-sm-regular text-text-secondary [&>p]:leading-[1.4] [&>p]:m-0">
        {children}
      </blockquote>
  },
  marks: {
    link: linkMarkHandler
  },
  types: {
    richTextTableBlock: ({
      value
    }: {
      value: RichTextTableBlock;
    }) => <DefaultTableBlock value={value} />
  },
  hardBreak: hardBreakHandler
};
function FaqAccordionBlock(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  const faqs = value.faqCollection?.faqs;
  if (!faqs || faqs.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== faqs) {
    t1 = faqs.map(_temp);
    $[0] = faqs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Accordion type="single" collapsible={true}>{t1}</Accordion>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(faq, index) {
  const title = getI18nString(faq.headline);
  return <AccordionItem key={faq.key ?? index} value={`faq-${index}`}><AccordionTrigger className="typography-body-sm-medium desktop:typography-body-md-medium text-text-primary [&>svg]:size-8">{title ?? "Untitled"}</AccordionTrigger><AccordionContent>{faq.bodyCopy ? <div className={cn(proseBaseClasses, "typography-body-sm-regular text-text-secondary", "[&>*+*]:mt-4 [&>*+h3]:mt-8 [&>*+[data-blockquote]]:mt-3 [&>[data-blockquote]+[data-blockquote]]:mt-0 [&>*+[data-table]]:mt-12")}><PortableText value={asPortableText(faq.bodyCopy)} components={faqBodyComponents} onMissingComponent={onMissingPortableTextComponent} /></div> : null}</AccordionContent></AccordionItem>;
}
const components: Partial<PortableTextReactComponents> = {
  block: {
    h3: ({
      children,
      value
    }) => {
      const slug = parseChildrenToSlug(value.children);
      return <h3 id={slug} className="typography-component-sm-medium desktop:typography-component-md-medium uppercase text-text-primary">
          {children}
        </h3>;
    },
    blockquote: ({
      children
    }) => <blockquote className="border-l border-border-default pl-3 typography-body-xs-regular desktop:typography-body-sm-regular text-text-secondary">
        {children}
      </blockquote>
  },
  marks: {
    link: linkMarkHandler
  },
  types: {
    accordion: ({
      value
    }: {
      value: AccordionBlockValue;
    }) => <FaqAccordionBlock value={value} />
  },
  hardBreak: hardBreakHandler
};
interface RichTextAccordionProps<T = unknown> {
  richText?: T | null;
  className?: string;
}
export function RichTextAccordion(t0) {
  const $ = _c(9);
  const {
    richText,
    className
  } = t0;
  if (!richText) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn(proseBaseClasses, "typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary", "[&>*+*]:mt-4 [&>*+h3]:mt-12", "[&_h3[data-state]]:contents", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== richText) {
    t2 = mergeAdjacentAccordions(asPortableText(richText));
    $[2] = richText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <PortableText value={t2} components={components} onMissingComponent={onMissingPortableTextComponent} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}